import * as React from 'react';
import { createFileRoute, useNavigate, Outlet } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { getCategories, syncCategories } from '@/web/lib/services';
import { addCategory, deleteCategory, updateCategory } from '@/web/lib/services'
import { type Category, type CategoryInput } from '@/types';
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen.tsx';
import { Button } from '@/web/components/atoms/button';
import { CategoriesNavCard } from '@/web/components/molecules/CategoriesNavCard';
import { CategoryFormModal } from '@/web/components/molecules/CategoryFormModal';
import { RiAddLine, RiLoopLeftLine } from 'react-icons/ri';

const CATEGORIES_QUERY_KEY = ['categories'];
const rootCategoriesQueryOptions = queryOptions({
  queryKey: CATEGORIES_QUERY_KEY,
  queryFn: getCategories,
  staleTime: 1000 * 60 * 5,
});

export const Route = createFileRoute('/categories')({
  loader: async ({ context }) => {
    return context.queryClient?.ensureQueryData(rootCategoriesQueryOptions);
  },
  preload: true,
  component: Categories,
  pendingComponent: LoadingScreen
});


function Categories() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'create' | 'edit'>('create');
  const [selectedCategory, setSelectedCategory] = React.useState<Category | null>(null);
  const [category, setCategory] = React.useState<CategoryInput>({
    name: '',
    imageUrl: '',
    parentId: null,
    description: '',
  });
  const queryClient = useQueryClient();
  const { error, data } = useSuspenseQuery(rootCategoriesQueryOptions);

  if (error) {
    console.log(error);
    console.log('----------- 33');
    navigate({ to: '/' });
    return null;
  }

  if (!data) {
    return <div>No data</div>;
  }

  const handleSelectCategory = (category: Category) => () => {
    navigate({ to: `/categories/${category.id}` });
  }

  const handleAddRootCategory = () => {
    setMode('create');
    setIsOpen(true);
    setCategory({
      name: '',
      imageUrl: '',
      parentId: null,
      description: '',
    });
  }

  const syncCategoriesMutation = useMutation({
    mutationFn: syncCategories,
    onSuccess: () => {
      console.log('Successfully synced categories');
    }
  });

  const handleCategoriesSync = () => {
    syncCategoriesMutation.mutate();
  }

  const addCategoryMutation = useMutation({
    mutationFn: addCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(rootCategoriesQueryOptions);
      setIsOpen(false);
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(rootCategoriesQueryOptions);
      setIsOpen(false);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries(rootCategoriesQueryOptions);
      setIsOpen(false);
    },
  });

  const handleDeleteCategory = (categoryUid: number) => {
    deleteCategoryMutation.mutate(categoryUid);
  }

  const handleSubmit = (data: any) => {
    const categoryData = {
      ...category,
      ...data,
    }
    if (mode === 'create') {
      console.log('creating...');
      addCategoryMutation.mutate(categoryData);
    } else {
      updateCategoryMutation.mutate({
        uid: category?.uid || 0,
        categoryInput: data,
      });
    }
  }


  const { categories } = data;
  return <div className="main-content mt-24">
    <div className="p-4">
      <div className="flex">
        <div className="w-[272px] flex-shrink-0">
          <CategoriesNavCard categories={categories} onSelectCategory={handleSelectCategory} selectedCategory={selectedCategory} />
          <Button className="mt-4 w-full" onClick={handleAddRootCategory}><RiAddLine /> Category</Button>
          <Button variant="outline" className="mt-4 w-full" onClick={handleCategoriesSync}><RiLoopLeftLine /> Sync Category</Button>
        </div>
        <div className="flex-1 px-8">
          <Outlet />
        </div>
      </div>
    </div>
    <CategoryFormModal
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      mode={mode}
      onSubmit={handleSubmit}
      onDelete={handleDeleteCategory}
      category={category}
    />
  </div>;
}

export default Categories;
